import * as MD from "reports/measures/MeasureDef";
import { AssessmentDetails, ObjectiveOutcomes, PracticeBetterSession, SubjectiveOutcomeWeekData } from "reports/ReportDataProcessorDef";
import * as T from "reports/measures/Trajectory";
import { UpdateMode, useDirtyState, useDirtyStateO } from "utility/DirtyStateManager";
import { useMemo } from "react";

// ---=== Parsed Report Types ===--- //

export type ReportWeek = MD.Month6TokenType | number;
// export type ReportWeekMap<T> = { [key in ReportWeek]: T; };

export type Discovery = string;
export type Discoveries = Discovery[];
export type EngagementLevel = { week: MD.MeasureWeek; value: number; };
export type Goal = { goal: string; detail: string; }
export type Goals = Goal[];
export type GoalsProgress = { week: MD.MeasureWeek; value: number; };
export type MedicationReductionStatus = { week: MD.MeasureWeek; value: number; label: string };
export type ReportWeekOptions = { label: string; value: ReportWeek }[];
export type ReturnToWorkStatus = { week: MD.MeasureWeek; value: number; label: string };
export type SubjectiveProgressSummary = string

export enum PayerEnums {
    KarunaHome = 0,
    Reclaim = 1,
    VA = 2
}

export enum BrandEnums {
    KarunaHome = 1,
    Reclaim = 2,
    Valor = 3
}

export enum PhysicianNotificationType {
    NewSymptoms = "NewSymptoms",
    PsychotherapyReferral = "PsychotherapyReferral",
    MedicationReEvaluation = "MedicationReEvaluation",
    NewInjury = "NewInjury"
}

export const physicianNotificationsOptions = [
    { label: "Psychotherapy Referral", value: PhysicianNotificationType.PsychotherapyReferral },
    { label: "Medication Re-Evaluation", value: PhysicianNotificationType.MedicationReEvaluation },
    { label: "New Injury", value: PhysicianNotificationType.NewInjury },
    { label: "New Symptoms", value: PhysicianNotificationType.NewSymptoms }
];

export const extensionWeekOptions = [
    { label: "1 Week", value: 1 },
    { label: "2 Weeks", value: 2 },
    { label: "3 Weeks", value: 3 },
    { label: "4 Weeks", value: 4 },
    { label: "5 Weeks", value: 5 },
    { label: "6 Weeks", value: 6 },
    { label: "7 Weeks", value: 7 },
    { label: "8 Weeks", value: 8 },
    { label: "9 Weeks", value: 9 },
    { label: "10 Weeks", value: 10 },
    { label: "11 Weeks", value: 11 },
    { label: "12 Weeks", value: 12 }
];

// ---=== Report Preparation Data ===---

export type ReportPreparationData = {
    // Preparation Info
    prepared_date: Date;
    preparer_name: string;
    status: string;
};

// ---=== Report Sample Report Data ===---

export type ReportSampleData = {
    // Sample Data Info
    sample_date: Date;
    sample_plan_weeks: number;
    sample_has_post_program_checkins: boolean;
    // Patient Info
    claim_number?: string;
    date_of_injury?: string;
    dob?: string;
    patient_id: number;
    patient_name: string;
    patient_name_masked: string;
    payer_type: PayerEnums;
    // Care Team Info
    coach?: string;
    has_physician_oversight_agreement?: boolean;
    physician?: string;
    provider?: string;
    // Program Info
    brand: BrandEnums;
    coaching_plan: string;
    coaching_plan_track_id: number;
    current_coaching_plan_week: number;
    program_type: string;
    session_count: number;
    // Practice Better Sessions
    practice_better_sessions: PracticeBetterSession[];
    // History of Injury
    condition_history?: Record<string, string[]>; // Keys are week numbers as strings
    // Subjective Outcomes
    subjective_outcomes?: Record<string, SubjectiveOutcomeWeekData>;
    // Objective Outcomes
    objective_outcomes?: ObjectiveOutcomes;
    // Assessments
    assessments?: Record<string, AssessmentDetails>; // Keys are week numbers as strings
};

// ---=== Report Generated Data ===---

export type ReportGeneratedInclusions = {
    // Subjective Outcomes
    engagementLevel?: boolean;
    goalsProgress?: boolean;
    // Objective Outcomes
    averageTime?: boolean;
    trajectories?: Record<MD.MeasureType, boolean>;
    medicationReduction?: boolean;
    returnToWork?: boolean;
    selectedImprovedTrajectoryTypes?: MD.MeasureTypes;
    sessionsAttended?: boolean;
    uniqueDays?: boolean;
    // Assessments
    behavioralAssessments?: boolean;
    continuedNeedsAssessments?: boolean;
    physicalAssessments?: boolean;
    socialAssessments?: boolean;
};

export type ReportGeneratedPlanSettings = {
    extensionWeeks: number;
    physicianNotification: boolean;
    physicianNotificationType: PhysicianNotificationType;
    recommendProgramContinuation: boolean;
    recommendProgramEnd: boolean;
    recommendProgramExtension: boolean;
};

export type ReportGeneratedData = {
    // Report Configuration
    assessmentsGenerated?: boolean;
    isGenerator?: boolean;
    reportWeek?: ReportWeek;
    reportPlanWeeks?: number;
    // History of Injury
    historyOfInjury?: string;
    // Subjective Outcomes
    discoveries?: Discoveries | null;
    engagementLevel?: EngagementLevel | null;
    goals?: Goals;
    goalsProgress?: GoalsProgress;
    subjectiveProgressSummary?: SubjectiveProgressSummary | null;
    // Assessment sections
    physicalAssessments?: string;
    behavioralAssessments?: string;
    socialAssessments?: string;
    continuedNeedsAssessments?: string;
    // Plan
    plan?: string;
    planSettings?: ReportGeneratedPlanSettings,
    // Inclusions
    inclusions?: ReportGeneratedInclusions;
};

export type ReportGeneratedPdfData = unknown;

export type DraftReport = ReportPreparationData & {
    id: number;
    sample_data: ReportSampleData;
    generated_data: ReportGeneratedData;
};

export type SubmittedReport = DraftReport & {};

export type ApprovedReport = SubmittedReport & {
    report_pdf: ReportGeneratedPdfData;
};

export type EditableReport = (DraftReport | SubmittedReport) & {};

export type ReportDataApiResponse = (EditableReport | ApprovedReport) & {
    error?: string;
};

// ---=== Report Rendering Types ===--- //

export type RenderReportHeaderPayload = {
    hasPhysicianOversight: boolean;
    isGenerator: boolean;
    reportWeek: ReportWeek;
    reportWeekOptions: ReportWeekOptions;
    // Callbacks
    reportWeekChangeCallback: (reportWeek: ReportWeek) => void;
};

export type RenderTrajectoryPayload = {
    disabled: boolean,
    included: boolean,
    trajectory: T.Trajectory
    reportWeek: ReportWeek,
    assessmentDecisions?: any[], // AssessmentDecision[] - using any to avoid circular dependency
    // Callbacks
    toggleTrajectoryCallback: (measureType: MD.MeasureType, add: boolean) => void
};

export type RenderKeyImprovedTrajectoryPayload = {
    trajectory: T.Trajectory;
    included: boolean;
    disabled: boolean,
    // Callbacks
    toggleTrajectoryCallback: (measureType: MD.MeasureType, add: boolean) => void
};

export type RenderKeyImprovedTrajectoriesPayload = RenderKeyImprovedTrajectoryPayload[];

export type RenderKeyImprovedTrajectoriesSectionPayload = {
    isGenerator: boolean;
    assessmentsGenerated: boolean;
    renderPayload: RenderKeyImprovedTrajectoriesPayload;
    reportWeek: ReportWeek;
};
